import { FC } from 'react'
import { Typography, makeStyles } from '@material-ui/core'
import moment from 'moment-timezone'
import i18n from '../../translations/i18n'
import config from '../../config/config'

const useStyles = makeStyles({
  card: {
    display: 'flex',
    gap: '14px',
    alignItems: 'center',
    background: '#fff',
    border: '1px solid #E7ECEF',
    borderRadius: '12px',
    padding: '10px',
    margin: '20px 0 0',
    width: '100%',
    boxSizing: 'border-box',
    textAlign: 'left'
  },
  image: {
    width: '92px',
    height: '72px',
    flexShrink: 0,
    borderRadius: '8px',
    objectFit: 'cover',
    background: '#E7ECEF'
  },
  imagePlaceholder: {
    width: '92px',
    height: '72px',
    flexShrink: 0,
    borderRadius: '8px',
    background: '#E7ECEF'
  },
  body: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  address: {
    fontWeight: 700,
    fontSize: '15px',
    lineHeight: 1.25,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  details: {
    fontSize: '13px',
    color: '#4A4F55'
  }
})

// Same URL scheme as apartments-api getPublicUrl (public catalog images)
const publicMediaUrl = (key: string): string | null => {
  const parts = key.split('/')
  if (parts.length < 3) return null
  const { BUCKET, REGION } = config.s3.transcodedMedia
  return `https://${BUCKET}.s3.${REGION}.amazonaws.com/apartments/${parts[1]}/${parts[2]}`
}

// "Myllärintie 7 as 2" → "Myllärintie 7" (same rule as apartments-api getUnspecificAddress)
const streetAndNumber = (address: string): string => {
  const match = address.match(/\d+/)
  return match ? address.slice(0, (match.index || 0) + match[0].length) : address
}

interface Props {
  apartment: any
  listing?: any
}

const ApartmentSummaryCard: FC<Props> = ({ apartment, listing }) => {
  const styles = useStyles()
  const fullAddress = listing?.information?.address || apartment?.address
  if (!fullAddress) return null
  const address = streetAndNumber(fullAddress)
  const rooms = listing?.information?.rooms || apartment?.rooms
  const kitchen = listing?.information?.kitchen || apartment?.kitchen
  const squareMeters = listing?.information?.squareMeters
  const price = listing?.confirmation?.price ?? apartment?.price
  const availableFromDate = listing?.confirmation?.availableFromDate
  const firstKey = Array.isArray(listing?.media)
    ? listing.media.find((item: any) => typeof item?.key === 'string')?.key
    : undefined
  const imageUrl = firstKey ? publicMediaUrl(firstKey) : null
  const layout = [rooms, kitchen].filter(Boolean).join(' + ')
  const specs = [
    layout,
    squareMeters ? `${squareMeters} m²` : null,
    price ? `${price} ${i18n.t('newApplication.summaryPerMonth')}` : null
  ]
    .filter(Boolean)
    .join('  ·  ')
  const available = listing?.confirmation
    ? `${i18n.t('apartment.list.confirmation.availableFromDate')}: ${
        availableFromDate
          ? moment(availableFromDate).format('D.M.YYYY')
          : i18n.t('apartment.list.confirmation.availableFromDateNegotiate')
      }`
    : null
  return (
    <div className={styles.card}>
      {imageUrl ? (
        <img className={styles.image} src={imageUrl} alt='' loading='lazy' />
      ) : (
        <div className={styles.imagePlaceholder} />
      )}
      <div className={styles.body}>
        <Typography component='div' className={styles.address} title={fullAddress}>
          {address}
        </Typography>
        {specs && (
          <Typography component='div' className={styles.details}>
            {specs}
          </Typography>
        )}
        {available && (
          <Typography component='div' className={styles.details}>
            {available}
          </Typography>
        )}
      </div>
    </div>
  )
}

export default ApartmentSummaryCard
